import {
    MAX_BOOSTER_BULLSEYE,
    MAX_BOOSTER_JOKER,
    MAX_BOOSTER_PLUSFIVE,
    MAX_BOOSTER_SHUFFLE,
    MAX_BOOSTER_TNT,
    MAX_BOOSTER_UNDO,
    MAX_COINS,
    MAX_EXTRA_CARDS,
    MAX_LIVES,
} from '../../Constants/Player';
import { MathHelper } from '../../Utility/MathHelper';
import { isNumber } from '../../Utility/Validator';
import { IPlayerState } from './IPlayerState';
import { setStateTaskData } from './PlayerStateHelper';
import { PlayerTask } from './PlayerTasks';
import { updateLifeTimer } from './PlayerTimerHelper';
import { track, TrackMethod } from '../../Utility/Tracking/Track';

export function spendCoins(state: IPlayerState, coins: number): IPlayerState {
    return setCoins(state, state.coins - coins);
}

export function setLives(
    state: IPlayerState,
    lives: number,
    queueTask: boolean = true
): IPlayerState {
    if (!isNumber(lives)) {
        return state;
    }

    lives = MathHelper.clamp(lives, 0, MAX_LIVES);

    if (lives > state.lives) {
        track('tLivesReceived', lives - state.lives, TrackMethod.ADD);
        if (queueTask) {
            state = setStateTaskData(state, {
                task: PlayerTask.LIVE,
                lives: lives - state.lives,
                previousLives: state.lives,
            });
        }
    }

    state = {
        ...state,
        lives: lives,
    };

    state = updateLifeTimer(state);

    return state;
}

export function setCoins(
    state: IPlayerState,
    coins: number,
    queueTask: boolean = true
): IPlayerState {
    if (!isNumber(coins)) {
        return state;
    }

    coins = MathHelper.clamp(coins, 0, MAX_COINS);

    if (coins > state.coins) {
        track('tCoinsReceived', coins - state.coins, TrackMethod.ADD);
        // todo tracking for fail flow?
        if (queueTask) {
            state = setStateTaskData(state, {
                task: PlayerTask.EARN_COINS,
                coins: coins - state.coins,
                previousCoins: state.coins,
            });
        }
    } else {
        track('tCoinsUsed', state.coins - coins, TrackMethod.ADD);
        if (queueTask) {
            state = setStateTaskData(state, {
                task: PlayerTask.SPEND_COINS,
                coins: state.coins - coins,
                previousCoins: state.coins,
            });
        }
    }

    state = {
        ...state,
        coins: coins,
        previousCoinsValue: state.coins,
    };

    return state;
}

export function setKeys(state: IPlayerState, keys: number): IPlayerState {
    state = { ...state };

    if (keys > state.keys) {
        state = setStateTaskData(state, {
            task: PlayerTask.KEY,
            keys: keys - state.keys,
            previousKeys: state.keys,
        });
    }

    return {
        ...state,
        keys: keys,
    };
}

export function setBoosterPlusFive(
    state: IPlayerState,
    amount: number
): IPlayerState {
    if (!isNumber(amount)) {
        return state;
    }

    amount = MathHelper.clamp(amount, 0, MAX_BOOSTER_PLUSFIVE);

    if (amount > 0) {
        track('tLevelExtraCardsReceived', 5, TrackMethod.ADD);
    }

    return {
        ...state,
        inventory: {
            ...state.inventory,
            boosterPlusFive: amount,
        },
    };
}

export function setBoosterTNT(
    state: IPlayerState,
    amount: number,
    queueTask: boolean = true
): IPlayerState {
    if (!isNumber(amount)) {
        return state;
    }

    amount = MathHelper.clamp(amount, 0, MAX_BOOSTER_TNT);

    if (amount > state.inventory.boosterTNT && queueTask) {
        state = setStateTaskData(state, {
            task: PlayerTask.TNT,
            amount: amount - state.inventory.boosterTNT,
            previousLives: state.lives,
        });
    }

    return {
        ...state,
        inventory: {
            ...state.inventory,
            boosterTNT: amount,
        },
    };
}

export function setBoosterBullsEye(
    state: IPlayerState,
    amount: number,
    queueTask: boolean = true
): IPlayerState {
    if (!isNumber(amount)) {
        return state;
    }

    amount = MathHelper.clamp(amount, 0, MAX_BOOSTER_BULLSEYE);

    if (amount > state.inventory.boosterBullsEye && queueTask) {
        state = setStateTaskData(state, {
            task: PlayerTask.BULLS_EYE,
            amount: amount - state.inventory.boosterBullsEye,
        });
    }
    return {
        ...state,
        inventory: {
            ...state.inventory,
            boosterBullsEye: amount,
        },
    };
}

export function setBoosterShuffle(
    state: IPlayerState,
    amount: number,
    queueTask: boolean = true
): IPlayerState {
    if (!isNumber(amount)) {
        return state;
    }

    amount = MathHelper.clamp(amount, 0, MAX_BOOSTER_SHUFFLE);

    if (amount > state.inventory.boosterShuffle && queueTask) {
        state = setStateTaskData(state, {
            task: PlayerTask.SHUFFLE,
            amount: amount - state.inventory.boosterShuffle,
        });
    }
    return {
        ...state,
        inventory: {
            ...state.inventory,
            boosterShuffle: amount,
        },
    };
}

export function setBoosterUndo(
    state: IPlayerState,
    amount: number
): IPlayerState {
    if (!isNumber(amount)) {
        return state;
    }

    amount = MathHelper.clamp(amount, 0, MAX_BOOSTER_UNDO);

    return {
        ...state,
        inventory: {
            ...state.inventory,
            boosterUndo: amount,
        },
    };
}

export function setBoosterJoker(
    state: IPlayerState,
    amount: number,
    queueTask: boolean = true
): IPlayerState {
    if (!isNumber(amount)) {
        return state;
    }

    amount = MathHelper.clamp(amount, 0, MAX_BOOSTER_JOKER);

    if (amount > state.inventory.boosterJoker && queueTask) {
        state = setStateTaskData(state, {
            task: PlayerTask.JOKER,
            amount: amount - state.inventory.boosterJoker,
        });
    }
    return {
        ...state,
        inventory: {
            ...state.inventory,
            boosterJoker: amount,
        },
    };
}

export function setExtraCards(
    state: IPlayerState,
    cards: number,
    queueTask: boolean = true
): IPlayerState {
    if (!isNumber(cards)) {
        return state;
    }

    cards = MathHelper.clamp(cards, 0, MAX_EXTRA_CARDS);

    const diff: number = cards - state.inventory.extraCards;
    if (diff > 0 && diff < 4 && queueTask) {
        // 1, 2 or 3 cards
        state = setStateTaskData(state, {
            task: PlayerTask.EXTRA_CARDS,
            cards: diff,
        });
    }
    if (diff > 0) {
        track('tMapExtraCardsReceived', diff, TrackMethod.ADD);
    }

    return {
        ...state,
        inventory: {
            ...state.inventory,
            extraCards: cards,
        },
    };
}
